import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";
import dayjs, {Dayjs} from "dayjs";
import "./TimeSlotModal.css";
import {TimeSlot} from "../../data/timeSlots";
import {updateTimeSlot} from "../../services/scheduleService";

// Form uses Dayjs for the date fields
interface TimeSlotForm extends Omit<TimeSlot, "StartsAt" | "EndsAt"> {
    StartsAt: Dayjs;
    EndsAt: Dayjs;
}

interface EditTimeSlotModalProps {
    isOpen: boolean;
    onClose: () => void;
    timeSlots: TimeSlot[];
    eventId: string;
    eventStart: string;
    eventEnd: string;
    initialSlot?: TimeSlot | null;
}

const EditTimeSlotModal: React.FC<EditTimeSlotModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 timeSlots,
                                                                 eventId,
                                                                 initialSlot = null
                                                             }) => {
    const [selectedId, setSelectedId] = useState<string>("");
    const [slot, setSlot] = useState<TimeSlotForm | null>(null);
    const [errors, setErrors] = useState({
        Title: "",
        StartsAt: "",
        EndsAt: ""
    });
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);


    useEffect(() => {
        if (!isOpen) return;

        if (initialSlot) {
            // Gray-cell path → load that slot immediately
            setSelectedId(initialSlot.TimeSlotId ?? "");
            setSlot({
                ...initialSlot,
                StartsAt: dayjs(initialSlot.StartsAt),
                EndsAt: dayjs(initialSlot.EndsAt),
            });
        } else {
            // Options-menu path → reset so you see the dropdown
            setSelectedId("");
            setSlot(null);
            setErrors({Title: "", StartsAt: "", EndsAt: ""});
            setSubmitError(null);
        }
    }, [isOpen, initialSlot])

    // after your [isOpen, initialSlot] effect
    useEffect(() => {
        // only run when we’re in the dropdown path
        if (!initialSlot && selectedId) {
            const found = timeSlots.find((t) => t.TimeSlotId === selectedId);
            if (found) {
                setSlot({
                    ...found,
                    StartsAt: dayjs(found.StartsAt),
                    EndsAt:   dayjs(found.EndsAt),
                });
                setErrors({ Title: "", StartsAt: "", EndsAt: "" });
                setSubmitError(null);
            }
        }
    }, [selectedId, initialSlot, timeSlots]);

    // Simple field validation
    const validate = () => {
        if (!slot) return false;
        const errs = {Title: "", StartsAt: "", EndsAt: ""};
        if (!slot.Title.trim()) errs.Title = "Title is required.";
        if (!slot.StartsAt) errs.StartsAt = "Start time is required.";
        if (!slot.EndsAt) errs.EndsAt = "End time is required.";
        if (slot.StartsAt && slot.EndsAt && slot.StartsAt >= slot.EndsAt)
            errs.EndsAt = "End must be after start.";
        setErrors(errs);
        return !Object.values(errs).some(Boolean);
    };

    // Submit updated slot
    const handleSubmit = async () => {
        if (!slot || !validate()) return;
        setLoading(true);
        setSubmitError(null);

        const payload: TimeSlot = {
            TimeSlotId: slot.TimeSlotId,
            Title: slot.Title,
            StartsAt: slot.StartsAt.toISOString(),
            EndsAt: slot.EndsAt.toISOString(),
            EventId: slot.EventId
        };

        try {
            await updateTimeSlot(payload);
            onClose();
        } catch (e: any) {
            setSubmitError(e.message || "Failed to update time slot.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="edit-timeslot">
            <Box className="modalBox">
                <Typography variant="h6">Edit Time Slot</Typography>

                {/* Dropdown selector */}
                {!initialSlot && (
                    <Select
                        className="formField"
                        fullWidth
                        value={selectedId}
                        onChange={(e) => setSelectedId(e.target.value as string)}
                        displayEmpty
                    >
                        <MenuItem value="" disabled>
                            -- Select a Time Slot --
                        </MenuItem>
                        {timeSlots.map((t) => (
                            <MenuItem key={t.TimeSlotId} value={t.TimeSlotId}>
                                {t.Title} ({new Date(t.StartsAt).toLocaleString()})
                            </MenuItem>
                        ))}
                    </Select>
                )}

                {/* Once a slot is chosen, show the edit form */}
                {slot && (
                    <>
                        <TextField
                            className="formField"
                            sx={{ mb: 2 }}
                            fullWidth
                            label="Title"
                            value={slot.Title}
                            onChange={(e) =>
                                setSlot((s) => (s ? {...s, Title: e.target.value} : null))
                            }
                            error={!!errors.Title}
                            helperText={errors.Title}
                        />

                        <DateTimePicker
                            className="formField"
                            sx={{ mb: 2 }}
                            label="Starts At"
                            value={slot.StartsAt}
                            onChange={(dt) =>
                                setSlot((s) =>
                                    s ? {...s, StartsAt: dt || s.StartsAt} : null
                                )
                            }
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors.StartsAt,
                                    helperText: errors.StartsAt
                                }
                            }}
                        />

                        <DateTimePicker
                            className="formField"
                            sx={{ mb: 2 }}
                            label="Ends At"
                            value={slot.EndsAt}
                            onChange={(dt) =>
                                setSlot((s) => (s ? {...s, EndsAt: dt || s.EndsAt} : null))
                            }
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!errors.EndsAt,
                                    helperText: errors.EndsAt
                                }
                            }}
                        />
                    </>
                )}
                {submitError && (
                    <Typography className="errorText">{submitError}</Typography>
                )}

                <Box className="actions">
                    <Button onClick={onClose} sx={{mr: 1}}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        className="editOrAdd"
                        disabled={loading || !slot}
                    >
                        {loading ? "Saving..." : "Save"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditTimeSlotModal;